import { useEffect, useState } from "react";
import { Truck, Printer, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

export const ShippingPanel = ({ order, onUpdated }) => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/shipping/status").then((r) => setStatus(r.data)).catch(() => setStatus({ configured: false }));
  }, []);

  const shipping = order.shipping || {};

  const book = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/orders/${order.id}/ship`);
      toast.success(`Shipment booked — AWB ${data.awb} (${data.courier || "courier"})`);
      onUpdated();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const label = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/orders/${order.id}/label`);
      window.open(data.label_url, "_blank", "noopener,noreferrer");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const track = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/admin/orders/${order.id}/tracking`);
      const current = data?.tracking_data?.shipment_track?.[0]?.current_status || "No courier activity yet";
      toast.success(`Live status: ${current}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5" data-testid="shipping-panel">
      <p className="text-sm font-medium text-[#111827] mb-3 flex items-center gap-2">
        <Truck size={15} className="text-[#E80071]" /> Shipping — Shiprocket
      </p>
      {!status ? null : !status.configured ? (
        <p className="text-xs text-gray-500 leading-relaxed" data-testid="shipping-not-configured">
          Ready but paused — add SHIPROCKET_EMAIL, SHIPROCKET_PASSWORD and SHIPROCKET_PICKUP_LOCATION to the backend
          environment, then book shipments and print labels from here.
        </p>
      ) : shipping.awb ? (
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-sm text-gray-700">AWB <span className="font-semibold">{shipping.awb}</span> · {shipping.courier}</p>
          <button onClick={label} disabled={busy} data-testid="ship-label-btn" className="flex items-center gap-2 bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors shadow-sm disabled:opacity-50">
            <Printer size={13} /> Print Label
          </button>
          <button onClick={track} disabled={busy} data-testid="ship-track-btn" className="flex items-center gap-2 bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors shadow-sm disabled:opacity-50">
            <RefreshCw size={13} /> Track
          </button>
        </div>
      ) : (
        <button onClick={book} disabled={busy} data-testid="ship-book-btn" className="flex items-center gap-2 bg-[#E80071] hover:bg-[#D00065] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm disabled:opacity-50">
          <Truck size={14} /> {busy ? "Booking…" : "Book Shipment & Get AWB"}
        </button>
      )}
    </div>
  );
};
